// Field definitions mirror the fallback chains in the festival-app repo:
// constants/Colors.ts, constants/LineupColors.ts, constants/ClashfinderColors.ts
// (assets/colors.json, assets/lineup_colors.json, assets/clashfinder_colors.json)

export type ColorFallback =
  | { type: "literal"; value: string }
  | { type: "field"; key: string };

export interface ColorFieldDef {
  key: string;
  label: string;
  section: string;
  fallback: ColorFallback;
  /** Field's value is always equal to another field's resolved value - not independently overridable. */
  derivedFrom?: string;
  helperText?: string;
}

const BASE_DEFAULTS = {
  primaryColor: "#5734AC",
  primaryColorDark: "#3F2277",
  primaryColorDarker: "#1F1140",
  primaryColorText: "#FFFFFF",
  alternativeTextColor: "#000000",
};

const baseFields = (): ColorFieldDef[] => [
  {
    key: "primaryColor",
    label: "Primary Color",
    section: "Base Theme",
    fallback: { type: "literal", value: BASE_DEFAULTS.primaryColor },
  },
  {
    key: "primaryColorDark",
    label: "Primary Color (Dark)",
    section: "Base Theme",
    fallback: { type: "literal", value: BASE_DEFAULTS.primaryColorDark },
  },
  {
    key: "primaryColorDarker",
    label: "Primary Color (Darker)",
    section: "Base Theme",
    fallback: { type: "literal", value: BASE_DEFAULTS.primaryColorDarker },
  },
  {
    key: "primaryColorText",
    label: "Primary Color Text",
    section: "Base Theme",
    fallback: { type: "literal", value: BASE_DEFAULTS.primaryColorText },
  },
  {
    key: "alternativeTextColor",
    label: "Alternative Text Color",
    section: "Base Theme",
    fallback: { type: "literal", value: BASE_DEFAULTS.alternativeTextColor },
  },
];

export const HOME_COLOR_FIELDS: ColorFieldDef[] = [
  ...baseFields(),
  {
    key: "cardColor",
    label: "Card Color",
    section: "News & Cards",
    fallback: { type: "field", key: "primaryColor" },
  },
  {
    key: "newsImageBackgroundColor",
    label: "News Image Background",
    section: "News & Cards",
    fallback: { type: "literal", value: "black" },
  },
  {
    key: "newsItemContentTopBorderColor",
    label: "News Item Top Border",
    section: "News & Cards",
    fallback: { type: "field", key: "primaryColor" },
  },
  {
    key: "newsItemContentBottomBorderColor",
    label: "News Item Bottom Border",
    section: "News & Cards",
    fallback: { type: "field", key: "primaryColor" },
  },
  {
    key: "newsDetailsBackgroundColor",
    label: "News Details Background",
    section: "News & Cards",
    fallback: { type: "field", key: "primaryColorDarker" },
    helperText: "Not shown in this preview - used on the News article detail screen",
  },
  {
    key: "homeListBackgroundColor",
    label: "Home List Background",
    section: "News & Cards",
    fallback: { type: "field", key: "primaryColorDarker" },
  },
  {
    key: "countdownBackgroundColor",
    label: "Countdown Background",
    section: "Countdown",
    fallback: { type: "field", key: "primaryColor" },
  },
  {
    key: "countdownTopBorderColor",
    label: "Countdown Top Border",
    section: "Countdown",
    fallback: { type: "field", key: "primaryColor" },
  },
  {
    key: "countdownBottomBorderColor",
    label: "Countdown Bottom Border",
    section: "Countdown",
    fallback: { type: "field", key: "primaryColorDarker" },
  },
  {
    key: "countdownTextColor",
    label: "Countdown Text",
    section: "Countdown",
    fallback: { type: "field", key: "primaryColorText" },
  },
  {
    key: "headerButtonColor",
    label: "Header Button",
    section: "Header & Navigation",
    fallback: { type: "field", key: "primaryColorText" },
  },
  {
    key: "headerButtonBackgroundColor",
    label: "Header Button Background",
    section: "Header & Navigation",
    fallback: { type: "literal", value: "transparent" },
  },
  {
    key: "navigationMenuItemBackgroundColor",
    label: "Nav Menu Item Background",
    section: "Header & Navigation",
    fallback: { type: "literal", value: "transparent" },
    helperText: "Side menu list item background",
  },
  {
    key: "notificationsModalBackgroundColor",
    label: "Notifications Modal Background",
    section: "Notifications",
    fallback: { type: "field", key: "primaryColorDark" },
    helperText: "Not shown in this preview - used in permission request modals (Settings)",
  },
  {
    key: "notificationHistoryDividerColor",
    label: "Notification History Divider",
    section: "Notifications",
    fallback: { type: "field", key: "primaryColorText" },
    helperText: "Not shown in this preview - used on the Notification History screen",
  },
  {
    key: "sponsorsBackgroundColor",
    label: "Sponsors Background",
    section: "Sponsors",
    fallback: { type: "field", key: "primaryColor" },
    helperText: "Not shown in this preview - used on the dedicated Sponsors screen",
  },
  {
    key: "sponsorsBorderColor",
    label: "Sponsors Border",
    section: "Sponsors",
    fallback: { type: "field", key: "primaryColorDarker" },
    helperText: "Not shown in this preview - used on the dedicated Sponsors screen",
  },
  {
    key: "socialsBackgroundColor",
    label: "Socials Background",
    section: "Socials",
    fallback: { type: "field", key: "primaryColorDarker" },
    helperText: "See the Band tab for a preview - also used on News, Trader & Showcase detail screens",
  },
  {
    key: "socialsBottomBorderColor",
    label: "Socials Bottom Border",
    section: "Socials",
    fallback: { type: "field", key: "primaryColorText" },
    helperText: "See the Band tab for a preview - also used on News, Trader & Showcase detail screens",
  },
  {
    key: "artistDetailsBackgroundColor",
    label: "Artist Details Background",
    section: "Artist",
    fallback: { type: "field", key: "primaryColorDarker" },
    helperText: "See the Band tab for a preview",
  },
  {
    key: "priorityOneColor",
    label: "Priority 1",
    section: "Priority Colors",
    fallback: { type: "literal", value: "#E69F00" },
    helperText: "Used on artist tags & the Clashfinder grid",
  },
  {
    key: "priorityTwoColor",
    label: "Priority 2",
    section: "Priority Colors",
    fallback: { type: "literal", value: "#0072B2" },
    helperText: "Used on artist tags & the Clashfinder grid",
  },
  {
    key: "priorityThreeColor",
    label: "Priority 3",
    section: "Priority Colors",
    fallback: { type: "literal", value: "#009E73" },
    helperText: "Used on artist tags & the Clashfinder grid",
  },
  {
    key: "priorityFourColor",
    label: "Priority 4",
    section: "Priority Colors",
    fallback: { type: "literal", value: "#CC79A7" },
    helperText: "Used on artist tags & the Clashfinder grid",
  },
  {
    key: "mapboxStageMarkerCircleColor",
    label: "Map Stage Marker",
    section: "Map",
    fallback: { type: "literal", value: "rgba(255, 248, 236, 0.96)" },
    helperText: "Not shown in this preview - used on the interactive map",
  },
];

export const NAVIGATION_COLOR_FIELDS: ColorFieldDef[] = [
  ...baseFields(),
  {
    key: "navigationMenuItemBackgroundColor",
    label: "Nav Menu Item Background",
    section: "Menu Items",
    fallback: { type: "literal", value: "transparent" },
    helperText: "Background behind each row in the side menu",
  },
];

export const BAND_COLOR_FIELDS: ColorFieldDef[] = [
  ...baseFields(),
  {
    key: "artistDetailsBackgroundColor",
    label: "Artist Details Background",
    section: "Header",
    fallback: { type: "field", key: "primaryColorDarker" },
  },
  {
    key: "countdownTopBorderColor",
    label: "Header Top Border",
    section: "Header",
    fallback: { type: "field", key: "primaryColor" },
    helperText: "Shared with the Home countdown's top border",
  },
  {
    key: "countdownBottomBorderColor",
    label: "Header Bottom Border",
    section: "Header",
    fallback: { type: "field", key: "primaryColorDarker" },
    helperText: "Shared with the Home countdown's bottom border",
  },
  {
    key: "socialsBackgroundColor",
    label: "Socials Background",
    section: "Socials",
    fallback: { type: "field", key: "primaryColorDarker" },
  },
  {
    key: "socialsBottomBorderColor",
    label: "Socials Bottom Border",
    section: "Socials",
    fallback: { type: "field", key: "primaryColorText" },
  },
];

// lineup_colors.json also has "backgroundColor" and "alternativeTextColor" keys, but
// LineupColors.ts destructures them without anything in the app ever importing or
// reading them (grepped every consumer) - they're dead, so they're deliberately
// left out here rather than offering config that has no effect.
export const LINEUP_COLOR_FIELDS: ColorFieldDef[] = [
  ...baseFields().filter((field) => field.key !== "alternativeTextColor"),
  {
    key: "headerColor",
    label: "Header Background",
    section: "List",
    fallback: { type: "field", key: "primaryColor" },
  },
  {
    key: "searchColor",
    label: "Search Bar Background",
    section: "List",
    fallback: { type: "field", key: "primaryColor" },
  },
  {
    key: "rowColor",
    label: "Row Background",
    section: "List",
    fallback: { type: "field", key: "primaryColor" },
  },
  {
    key: "rowGroupColor",
    label: "Row Group Header Background",
    section: "List",
    fallback: { type: "field", key: "primaryColorDarker" },
  },
  {
    key: "rowGroupTextColor",
    label: "Row Group Header Text",
    section: "List",
    fallback: { type: "field", key: "primaryColorText" },
  },
  {
    key: "itemSeperatorBorder",
    label: "Row Separator",
    section: "List",
    fallback: { type: "field", key: "primaryColorDarker" },
  },
  {
    key: "dateColor",
    label: "Day Selector: Date",
    section: "Day Selector",
    fallback: { type: "field", key: "primaryColorText" },
  },
  {
    key: "dayColor",
    label: "Day Selector: Day",
    section: "Day Selector",
    fallback: { type: "field", key: "primaryColorText" },
  },
  {
    key: "dayUnderlineColor",
    label: "Day Selector: Underline",
    section: "Day Selector",
    fallback: { type: "field", key: "primaryColor" },
  },
  {
    key: "selectedDayUnderlineColor",
    label: "Day Selector: Selected Underline",
    section: "Day Selector",
    fallback: { type: "field", key: "primaryColorText" },
  },
];

// clashfinder_colors.json also has a "primaryColor" key, but it's destructured in
// ClashfinderColors.ts without anything ever importing or reading it - dead, so it's
// left out here (primaryColorDark/Darker/Text and alternativeTextColor are all
// genuinely consumed elsewhere in the Clashfinder screens).
export const CLASHFINDER_COLOR_FIELDS: ColorFieldDef[] = [
  ...baseFields().filter((field) => field.key !== "primaryColor"),
  {
    key: "backgroundColor",
    label: "Screen Background",
    section: "Grid",
    fallback: { type: "field", key: "primaryColorDark" },
    derivedFrom: "primaryColorDark",
    helperText: "Always matches Primary Color (Dark) - not independently set in the app",
  },
  {
    key: "clashfinderHeader",
    label: "Header Background",
    section: "Grid",
    fallback: { type: "field", key: "primaryColorDarker" },
  },
  {
    key: "stageHeader",
    label: "Stage Row Background",
    section: "Grid",
    fallback: { type: "field", key: "primaryColorDarker" },
  },
];

export const PRIORITY_COLOR_KEYS = [
  "priorityOneColor",
  "priorityTwoColor",
  "priorityThreeColor",
  "priorityFourColor",
] as const;

export type ImageFieldKey =
  | "home_logo"
  | "home_background"
  | "menu_background"
  | "menu_logo"
  | "placeholder"
  | "thumbnail";

export interface ImageFieldDef {
  key: ImageFieldKey;
  label: string;
  helperText: string;
}

export const IMAGE_FIELDS: ImageFieldDef[] = [
  {
    key: "home_background",
    label: "Home Header Background",
    helperText: "Shown behind the logo at the top of the Home screen",
  },
  {
    key: "home_logo",
    label: "Home Logo",
    helperText: "Shown over the Home header background",
  },
  {
    key: "menu_background",
    label: "Side Menu / Nav Bar Background",
    helperText: "Background image behind the side menu (nav drawer)",
  },
  {
    key: "menu_logo",
    label: "Side Menu Logo",
    helperText: "Shown at the top of the side menu (nav drawer)",
  },
  {
    key: "placeholder",
    label: "Placeholder Image",
    helperText:
      "Generic fallback shown when no photo is set - used on Home (news & up next cards) and the Band screen header",
  },
  {
    key: "thumbnail",
    label: "Artist Thumbnail",
    helperText: "Fallback artist photo shown in each Lineup row when a band has no image",
  },
];
